import React, { useCallback, useMemo, useEffect } from 'react'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { OccupancyOption } from 'tripPlanner/pages/trip/GenerateTripPage'
import OccupancyPickerVertical from 'components/Common/OccupancyPicker/OccupancyPickerVertical'
import * as Analytics from 'analytics/analytics'

interface Props {
  occupancy: OccupancyOption
  onOccupancySelect: (occupancy: OccupancyOption) => void
  stepNumber: number
  totalSteps: number
  onNext?: () => void
  onUpdateAllLocationsOccupancy?: (occupancy: OccupancyOption) => void
}

function AITripOccupancySelection({
  occupancy,
  onOccupancySelect,
  stepNumber,
  totalSteps,
  onNext,
  onUpdateAllLocationsOccupancy,
}: Props) {
  const intl = useIntl()
  const occupanciesArray = useMemo(() => {
    return [{
      adults: occupancy.adults,
      children: occupancy.children?.length || 0,
      childrenAge: occupancy.children || [],
    }]
  }, [occupancy])

  const handleOccupancyChange = useCallback((newOccupancies: Array<App.Occupants>) => {
    const newOccupancy = newOccupancies[0]
    const occupancyOption: OccupancyOption = {
      adults: newOccupancy.adults,
      children: newOccupancy.childrenAge,
    }
    onOccupancySelect(occupancyOption)
    onUpdateAllLocationsOccupancy?.(occupancyOption)
  }, [onOccupancySelect, onUpdateAllLocationsOccupancy])

  const handleSubmit = useCallback<React.FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault()
    onNext?.()
  }, [onNext])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'travellers',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  const stepText = intl.formatMessage(
    {
      id: 'ewX/UMLdx4',
      defaultMessage: '{stepNumber} of {totalSteps}',
      description: 'Step indicator showing current step number and total steps',
    },
    { stepNumber, totalSteps },
  )

  const headingText = intl.formatMessage(
    {
      id: 'IetHcHEdPy',
      defaultMessage: '<i>Travellers</i>',
      description: 'Heading text with italicized Travellers',
    },
    {
      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
    },
  )

  const descriptionText = intl.formatMessage({
    id: 'zU2Mra97DK',
    defaultMessage: 'Who are travelling?',
    description: 'Question asking about who is travelling',
  })

  const childrenAgeLabel = intl.formatMessage({
    id: 'T3KWGkcE0d',
    defaultMessage: 'Age at time of trip',
    description: 'Label for children age input',
  })

  return (
    <form onSubmit={handleSubmit} id="occupancy-form">
      <Group direction="vertical" gap={20} desktopGap={24}>
        <VerticalSpacer gap={8}>
          <BodyText variant="large" weight="bold">{stepText}</BodyText>
          <Heading variant="heading1">
            {headingText}
          </Heading>
          <BodyText variant="large">
            {descriptionText}
          </BodyText>
        </VerticalSpacer>
        <OccupancyPickerVertical
          occupancies={occupanciesArray}
          onChange={handleOccupancyChange}
          childrenAgeLabel={childrenAgeLabel}
          showChildrenSelectLabel={false}
        />
      </Group>
    </form>
  )
}

export default AITripOccupancySelection
